import { BaserowClient } from '../../client/baserow-client';
import { UserOperations } from '../../client/user-operations';
import { ConfigManager } from '../config/manager';
import { configSet } from './config';
import type { GlobalProjectConfig } from '../config/types';

/**
 * Handles user logout from Baserow.
 * @param options Command options including local flag
 * @returns An object with status and config location
 * @throws If logout fails
 */
export async function logout(options?: { local?: boolean }): Promise<{
  status: 'success' | 'not_logged_in';
  location: string;
  message: string;
}> {
  const config = ConfigManager.getInstance();
  const useGlobalConfig = !options?.local;
  const configOptions = { global: useGlobalConfig };
  let resolvedConfig;
  if (useGlobalConfig) {
    const globalConfig = config.getGlobalConfig();
    const currentDir = process.cwd();
    let projectConfig: GlobalProjectConfig | undefined;
    const projects = Object.entries(globalConfig.projects || {});
    for (const [, project] of projects) {
      if (project.path === currentDir) {
        projectConfig = project;
        break;
      }
    }
    resolvedConfig = projectConfig || {};
  } else {
    resolvedConfig = config.getLocalConfig() || {};
  }
  if (!resolvedConfig.token) {
    const location = useGlobalConfig ? 'global' : 'local';
    return {
      status: 'not_logged_in',
      location,
      message: `Not currently logged in (no token found in ${location} config)`
    };
  }
  const refreshToken = resolvedConfig.refreshToken;
  const tokenType = resolvedConfig.tokenType;
  try {
    if (refreshToken && resolvedConfig.url && tokenType === 'JWT') {
      const client = new BaserowClient({
        url: resolvedConfig.url,
        token: resolvedConfig.token,
        tokenType: 'JWT'
      });
      const userOperations = new UserOperations(client);
      try {
        await userOperations.logout(refreshToken);
      } catch (err) {
        // If token blacklisting fails, just continue with local cleanup
      }
    }
    await configSet('token', '', configOptions);
    await configSet('refreshToken', '', configOptions);
    await configSet('userEmail', '', configOptions);
    await configSet('userFirstName', '', configOptions);
    const location = useGlobalConfig ? 'global' : 'local';
    return {
      status: 'success',
      location,
      message: `Successfully logged out (removed credentials from ${location} config)`
    };
  } catch (err: unknown) {
    if (err instanceof Error) {
      throw new Error(`Logout failed: ${err.message}`);
    } else {
      throw new Error('Logout failed: An unknown error occurred');
    }
  }
} 